/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Font, StyleSheet, Document, Page, View, Text, Image, Svg, Rect, Polyline } from '@react-pdf/renderer';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const source = require('src/webfonts/khmer-regular.ttf');
Font.register({
  family: 'Khmer',
  src: source,
});

// eslint-disable-next-line @typescript-eslint/no-var-requires
const headerLogoImage = require('src/assets/contract_header.png');

const styles = StyleSheet.create({
  page: {
    paddingTop: 25,
    paddingBottom: 25,
    paddingLeft: 35,
    paddingRight: 35,
    fontSize: 9.5,
    fontFamily: 'Khmer',
    color: '#111',
    lineHeight: 1.45,
  },
  headerImage: {
    width: 220,
    height: 40,
    marginBottom: 8,
  },
  topHeaderLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    marginBottom: 10,
  },
  docTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 2,
    marginBottom: 3,
  },
  docVersion: {
    fontSize: 10.5,
    textAlign: 'center',
    marginBottom: 12,
  },
  redText: {
    color: '#e53935',
    fontWeight: 'bold',
  },
  introBlock: {
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: '#eee',
  },
  introKhmer: {
    fontSize: 9,
    marginBottom: 4,
    textAlign: 'justify',
    lineHeight: 1.45,
  },
  introEng: {
    fontSize: 8.5,
    color: '#333',
    textAlign: 'justify',
    lineHeight: 1.4,
  },
  pointRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
    paddingVertical: 6,
  },
  pointNum: {
    width: 25,
    fontSize: 9.5,
    fontWeight: 'bold',
  },
  pointKhmer: {
    width: '45%',
    paddingRight: 20,
    fontSize: 8.8,
    lineHeight: 1.45,
  },
  pointEng: {
    width: '45%',
    paddingLeft: 10,
    fontSize: 8.4,
    color: '#333',
    lineHeight: 1.4,
  },
  feeTable: {
    width: '100%',
    borderWidth: 0.5,
    borderColor: '#b0bec5',
    marginVertical: 4,
  },
  feeHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 0.5,
    borderBottomColor: '#b0bec5',
    fontWeight: 'bold',
  },
  feeRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
    paddingVertical: 3,
  },
  colCode: {
    width: '6%',
    paddingLeft: 4,
    fontSize: 8.5,
  },
  colDescKhmer: {
    width: '40%',
    paddingRight: 15,
    fontSize: 8.5,
  },
  colDescEng: {
    width: '40%',
    paddingLeft: 10,
    fontSize: 8,
    color: '#333',
  },
  colRate: {
    width: '14%',
    paddingRight: 6,
    fontSize: 9,
    textAlign: 'right',
  },
  agreementBox: {
    borderWidth: 0.5,
    borderColor: '#888',
    padding: 10,
    marginVertical: 10,
    backgroundColor: '#fafafa',
  },
  agreementLine: {
    flexDirection: 'row',
    marginBottom: 4,
    fontSize: 9.5,
    flexWrap: 'wrap',
  },
  monthTitle: {
    fontSize: 9.5,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 3,
  },
});

interface SellerContractPdfDocumentProps {
  contract: {
    supid: number;
    year: number;
    name?: string;
    contact?: {
      email1?: string;
      email2?: string;
      contact1?: string;
      contact2?: string;
      contact3?: string;
    };
    legalRep?: string;
  };
  details: any[];
}

export function SellerContractPdfDocument({ contract, details = [] }: SellerContractPdfDocumentProps) {
  const supid = contract.supid || '';
  const year = contract.year || new Date().getFullYear();
  const name = contract.name || '';
  const legalRep = contract.legalRep || '';
  const phone = contract.contact?.contact1 || contract.contact?.contact2 || '';
  const email = contract.contact?.email1 || contract.contact?.email2 || '';

  const getMonthDetail = (monthNum: number) => {
    return details.find(d => Number(d.month) === monthNum) || {};
  };

  const formatRate = (val: any, isPercent = false) => {
    if (val == null || val === '') return '';
    const str = String(val).trim();
    if (isPercent) {
      return str.endsWith('%') ? str : `${str}%`;
    }
    return str.startsWith('$') ? str : `$${str}`;
  };

  const renderHeader = () => (
    <View style={{ marginBottom: 6 }}>
      <Image src={headerLogoImage} style={styles.headerImage} />
      <View style={styles.topHeaderLine} />
    </View>
  );

  const isStandardRate = (val: any, standardVal: number) => {
    if (val == null || val === '') return true;
    const str = String(val)
      .replace(/[\$%]/g, '')
      .trim();
    const num = parseFloat(str);
    if (!isNaN(num)) {
      return Math.abs(num - standardVal) < 0.0001;
    }
    return false;
  };

  const renderNegotiatedFeeSection = (monthNum: number) => {
    const detail = getMonthDetail(monthNum);
    const monthStr = monthNum < 10 ? `0${monthNum}` : `${monthNum}`;
    const validWithin = `${year}-${monthStr}`;

    const rawComm = detail.commission;
    const rawHand = detail.handlingFee;
    const rawPick = detail.pickupFee;
    const rawStor = detail.storageFee;
    const rawProc = detail.processingFee;
    const rawRemv = detail.removalFee;

    const commission = isStandardRate(rawComm, 10) ? '' : formatRate(rawComm, true);
    const handling = isStandardRate(rawHand, 0.1) ? '' : formatRate(rawHand);
    const pickup = isStandardRate(rawPick, 0.15) ? '' : formatRate(rawPick);
    const storage = isStandardRate(rawStor, 0.33) ? '' : formatRate(rawStor);
    const processing = isStandardRate(rawProc, 0.02) ? '' : formatRate(rawProc);
    const removal = isStandardRate(rawRemv, 0.05) ? '' : formatRate(rawRemv);

    return (
      <View key={`month-${monthNum}`} style={{ marginBottom: 10 }}>
        <Text style={styles.monthTitle}>
          {`អត្រាចរចា (Negotiated fee rate) មានសុពលភាពក្នុងខែ (Valid within) `}
          <Text style={styles.redText}>{validWithin}</Text>
        </Text>
        <View style={styles.feeTable}>
          <View style={styles.feeHeader}>
            <Text style={styles.colCode}></Text>
            <Text style={styles.colDescKhmer}>{`ប្រភេទថ្ងៃសេវា`}</Text>
            <Text style={styles.colDescEng}>Types of fee</Text>
            <Text style={styles.colRate}>{`អត្រាចរចា Negotiated fee`}</Text>
          </View>

          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(1)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃបច្ចេកវិទ្យា និងសេវាកម្ម គិតជាភាគរយនៃថ្លៃទំនិញលក់បាន`}</Text>
            <Text style={styles.colDescEng}>Techonolgy and service as percentage of the sale price.</Text>
            <Text style={[styles.colRate, styles.redText]}>{commission}</Text>
          </View>

          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(2)</Text>
            <Text
              style={styles.colDescKhmer}
            >{`ថ្លៃវេចខ្ចប់ទំនិញក្នុងមួយទំនិញ សម្រាប់ការងារស្វែងរក ពិនិត្យ និងវេចខ្ចប់`}</Text>
            <Text style={styles.colDescEng}>Unit handling fee for checking, picking, and packing orders.</Text>
            <Text style={[styles.colRate, styles.redText]}>{handling}</Text>
          </View>

          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(3)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃទៅយកទំនិញក្នុងមួយទំនិញ គិតពីអ្នកលក់`}</Text>
            <Text style={styles.colDescEng}>Unit pickup fee</Text>
            <Text style={[styles.colRate, styles.redText]}>{pickup}</Text>
          </View>

          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(4)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃដាក់ស្តុកក្នុងឃ្លាំង`}</Text>
            <Text style={styles.colDescEng}>Inventory storage fee</Text>
            <Text style={[styles.colRate, styles.redText]}>{storage}</Text>
          </View>

          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(5)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃរៀបទំនិញចូលស្តុក`}</Text>
            <Text style={styles.colDescEng}>Inventory processing fee</Text>
            <Text style={[styles.colRate, styles.redText]}>{processing}</Text>
          </View>

          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(6)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃដកស្តុក`}</Text>
            <Text style={styles.colDescEng}>Inventory removal fee</Text>
            <Text style={[styles.colRate, styles.redText]}>{removal}</Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {renderHeader()}

        <Text style={styles.docTitle}>{`កិច្ចព្រមព្រៀងប្រើប្រាស់ប្រព័ន្ធសម្រាប់អ្នកលក់  `}</Text>
        <Text style={styles.docVersion}>
          {`កំណែ `}
          <Text style={styles.redText}>{year}-01-01</Text>
        </Text>

        <View style={styles.introBlock}>
          <Text style={styles.introKhmer}>
            {`នៅក្នុងកិច្ចព្រមព្រៀងនេះ អ្នកប្រើប្រាស់ប្រព័ន្ធដាក់លក់និងផ្សព្វផ្សាយទំនិញ (តាមគេហទំព័រ និងកម្មវិធីទូរស័ព្ទ) E-commerce L192 ត្រូវបានហៅថា “អ្នកលក់” យល់ព្រមប្រើប្រាស់ប្រព័ន្ធ E-commerce L192 បង្កើតដោយក្រុមហ៊ុន Little Digital (Cambodia) Co., Ltd និង យល់ព្រមទទួលលក្ខខណ្ឌ ដូចខាងក្រោម៖`}
          </Text>
          <Text style={styles.introEng}>
            In this agreement, as a user of the platforms (web and app), you hereinafter are SELLER. Little Digital
            (Cambodia) Co., Ltd develops, maintains, and promotes the platforms and hereinafter is PLATFORM. SELLER and
            PLATFORM agree on the following terms and conditions:
          </Text>
        </View>

        {/* Points 1 to 15 wrapped in template literal backticks */}
        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>1</Text>
          <Text style={styles.pointKhmer}>
            {`អ្នកលក់ត្រូវផ្តល់ព័ត៌មានអំពីប្រវត្តិរូបរបស់ខ្លួនដោយត្រឹមត្រូវ និងពិតប្រាកដ។`}
          </Text>
          <Text style={styles.pointEng}>
            SELLER shall provide PLATFORM with correct and true information regarding SELLER’s profile.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>2</Text>
          <Text
            style={styles.pointKhmer}
          >{`កិច្ចព្រមព្រៀងនេះមានសុពលភាពរហូតដល់មានការកែប្រែណាមួយនៅក្នុងប្រព័ន្ធ   `}</Text>
          <Text style={styles.pointEng}>
            This agreement is continuous until there is an update to the agreement by PLATFORM.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>3</Text>
          <Text style={styles.pointKhmer}>
            {`អ្នកលក់យល់ព្រមដាក់លក់ និងផ្សព្វផ្សាយទំនិញនៅលើគេហទំព័រនិងកម្មវិធីទូរស័ព្ទដោយ   
            ស្វ័យប្រវត្តិពេលអ្នកលក់ប្រើប្រព័ន្ធដើម្បីបញ្ចូលព័ត៌មានទំនិញនិងហាងអ្នកលក់។    
            អ្នកលក់ត្រូវអនុវត្តតាមការណែនាំរបស់ប្រព័ន្ធក្នុងការបញ្ចូលទំនិញ និងរៀបចំទំនិញ   
            ពេលមានការបញ្ជាទិញក្នុងរយៈពេលមិនលើសពី ១ម៉ោងធ្វើការប្រសិនបើទំនិញ    
            ត្រូវបានរក្សាទុកនៅទីតាំងរបស់អ្នកលក់។  `}
          </Text>
          <Text style={styles.pointEng}>
            SELLER agrees to list products on PLATFORM’s web and app through PLATFORM’s tools. SELLER shall do as
            specified by PLATFORM’s tools to upload products and prepare orders within one business hour if SELLER keeps
            its products at its site.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>4</Text>
          <Text style={styles.pointKhmer}>
            {`ទំនិញរបស់អ្នកលក់ត្រូវតែជាទំនិញស្របច្បាប់នៃព្រះរាជាណាចក្រកម្ពុជា។ 
            អ្នកលក់ត្រូវមានសិទ្ធិចែកចាយផលិតផលនិងកាន់កាប់កម្មសិទ្ធិបញ្ញាលើទំនិញ     
            ទាំងអស់នេះ។ ប្រព័ន្ធមិនទទួលខុសត្រូវចំពោះសកម្មភាពឬបណ្តឹងណាមួយពី    
            ភាគីទីបីចោទប្រកាន់អ្នកលក់ឡើយ។ ប្រព័ន្ធមានសិទ្ធិដកទំនិញរបស់អ្នកលក់ចេញ   
            ពីគេហទំព័រនិងកម្មវិធីទូរស័ព្ទឬឃ្លាំងក្រុមហ៊ុនប្រសិនបើមានការទាមទារឬចំណាត់      
            ការស្របច្បាប់ណាមួយ។ ប្រព័ន្ធក៏មានសិទ្ធិបដិសេធទំនិញណាដែលក្រុមហ៊ុនរក     
            ឃើញថាមិនសមស្របសម្រាប់ការដាក់លក់ផងដែរ។`}
          </Text>
          <Text style={styles.pointEng}>
            SELLER’s products must be legal by laws in Cambodia. SELLER shall have the right to distribute its products
            in Cambodia and own the intellectual property of its products. PLATFORM is not responsible for any action or
            claim by a third party against SELLER. PLATFORM has the right to remove SELLER’s products from its platforms
            and facilities if there is any legitimate claim or action against SELLER. PLATFORM also has the right to
            disapprove any SELLER’s product if PLATFORM finds the product inappropriate for its platforms.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>5</Text>
          <Text style={styles.pointKhmer}>
            {`អ្នកលក់មានសិទ្ធិកំណត់តម្លៃផលិតផលរបស់ខ្លួន។ ទោះជាយ៉ាងណា ប្រព័ន្ធមានសិទ្ធិបដិសេធតម្លៃដែលមិនសមហេតុផល និងអាចកំណត់ តម្លៃផ្តល់ជូនអតិបរមា (Maximum Offer Price) សម្រាប់ទំនិញណាមួយ។`}
          </Text>
          <Text style={styles.pointEng}>
            SELLER controls pricing of its own products. However, PLATFORM has the right to disapprove of pricing deemed
            unreasonable. PLATFORM can determine the maximum Offer Price for a particular product.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>6</Text>
          <Text style={styles.pointKhmer}>
            {`ប្រសិនបើទម្ងន់ ឬទំហំដែលអ្នកលក់បញ្ចូលក្នុងប្រព័ន្ធ ខុសពីទម្ងន់ ឬទំហំពិតប្រាកដ 
            ប្រព័ន្ធនឹងគិតថ្លៃដឹកជញ្ជូនបន្ថែមពីអ្នកលក់ ដោយផ្អែកលើការគណនាថ្លៃដឹកជញ្ជូន     
            តាមប្រព័ន្ធ。`}
          </Text>
          <Text style={styles.pointEng}>
            If the weight and dimensions of SELLER’s product on PLATFORM are different from the actual weight and
            dimensions, PLATFORM charges SELLER for additional delivery cost. PLATFORM uses its delivery cost calculator
            to determine the delivery cost.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>7</Text>
          <Text style={styles.pointKhmer}>
            {`ប្រសិនបើអ្នកលក់ដាក់ស្តុកទំនិញនៅឃ្លាំងក្រុមហ៊ុន ក្រុមហ៊ុនទទួលខុសត្រូវចំពោះ     
            ការបាត់បង់ ឬការខូចខាតស្តុក ហើយនឹងសងសំណងតាមតម្លៃដែលបាន     
            ព្រមព្រៀង។ ទោះយ៉ាងណា ក្រុមហ៊ុនមិនទទួលខុសត្រូវចំពោះការខូចខាត     
            ឬសឹករិកដោយធម្មជាតិដែលកើតឡើងពីការស្តុករយៈពេលយូរឬការតាំងបង្ហាញទំនិញ    
            ឡើយ។ អ្នកលក់អាចប្រើប្រព័ន្ធដើម្បីបង្កើតព័ត៌មានដាក់ស្តុកមុនពេលផ្ញើមកហើយក៏        
            អាចបង្កើតព័ត៌មានដកស្តុកទៅវិញ មុននឹងមកយកស្តុកទៅវិញ តាមប្រព័ន្ធដើម្បីជួយ     
            សម្រួលការងារនិងងាយស្រួលក្នុងការផ្ទៀងផ្ទាត់។ ប្រព័ន្ធអាចមិនទទួលដាក់ឬដក     
            ស្តុកណាដែលមិនមានការបង្កើតព័ត៌មានជាមុន។ ក្រុមហ៊ុនមានសិទ្ធិដកចេញទំនិញ     
            ដែលមិនអាចប្រើប្រាស់បាន ទំនិញរកឃើញថាហាមឃាត់និងខុសច្បាប់ ខូច     
            គុណភាព ខូចខាតផុតកំណត់ ឬមូលហេតុផ្សេងៗដែលប្រព័ន្ធចាំបាច់ត្រូវដក
            ចេញវិញ។ ដោយសារប្រព័ន្ធគ្រប់គ្រងស្តុកមានភាពសមុគស្មាញ ការរាប់ស្តុកនៅ     
            ឃ្លាំងមិនអាចអនុវត្តបានទេ។ ឃ្លាំងមាន ខ្យល់ចេញចូលធម្មជាតិ ប៉ុន្តែមិនមាន     
            ម៉ាស៊ីនត្រជាក់ទេ។`}
          </Text>
          <Text style={styles.pointEng}>
            If SELLER stores inventory at PLATFORM’s fulfillment facilities, PLATFORM is responsible for loss of and
            damage to SELLER’s inventory. PLATFORM shall pay for the loss and damage at negotiated prices. However,
            PLATFORM is not responsible for reasonable wear and tear due to product storage or display. Before sending
            inventory to PLATFORM, SELLER must create a shipment using PLATFORM’s tools. PLATFORM cannot process any
            inventory shipped by SELLER without a shipment record created by SELLER. PLATFORM shall inform SELLER of the
            shipment discrepancy. If SELLER wants to remove inventory from PLATFORM, SELLER must create a removal
            request. PLATFORM has the right to remove products which have become unusable because of illegality, defect,
            damage, overdue expiry date, and other reasons beyond our control. Due to complexity of PLATFORM’s inventory
            storing method, counting inventory at PLATFORM’s facilities is not possible. Storage facilities are
            naturally ventilated but not air-conditioned.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>8</Text>
          <Text style={styles.pointKhmer}>
            {`ប្រព័ន្ធផ្តល់មធ្យោបាយភ្ជាប់រវាង អ្នកលក់ អ្នកទិញ ប្រតិបត្តិករ និងអ្នកដឹកជញ្ជូន ដើម្បីធានាថាការទិញ លក់ និងការបំពេញការបញ្ជាទិញមានសុវត្ថិភាព    
            និងងាយស្រួល ប្រព័ន្ធទទួលខុសត្រូវ ទទួលប្រាក់ពីអ្នកទិញ ទូទាត់ប្រាក់ទៅអ្នកលក់ និងប្រតិបត្តិករនិងអ្នកដឹកជញ្ជូនបន្ទាប់ពីដកកម្រៃ។ ថ្លៃសេវារបស់ប្រព័ន្ធរួមមាន៖   `}
          </Text>
          <Text style={styles.pointEng}>
            PLATFORM is an intermediary which connects sellers, buyers, operators, and drivers to ensure the process of
            buying, selling, and fulfillment is 100% safe and most convenient for all the parties involved. As an
            intermediary, PLATFORM is responsible for (a) charging buyers and accepting payments from buyers, and (b)
            disbursing payments to sellers and operators involved less its fees. The following list describes PLATFORM’s
            fees.
          </Text>
        </View>

        {/* Standard Fee list under Point 8 */}
        <View style={styles.feeTable}>
          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(1)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃបច្ចេកវិទ្យានិងសេវាកម្ម គិតជាភាគរយនៃថ្លៃទំនិញលក់បាន`}</Text>
            <Text style={styles.colDescEng}>Techonolgy and service as percentage of the sale price.</Text>
            <Text style={styles.colRate}>10%</Text>
          </View>
          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(2)</Text>
            <Text
              style={styles.colDescKhmer}
            >{`ថ្លៃវេចខ្ចប់ទំនិញក្នុងមួយទំនិញ សម្រាប់ការងារស្វែងរក ពិនិត្យ និងវេចខ្ចប់`}</Text>
            <Text style={styles.colDescEng}>Unit handling fee for checking, picking, and packing orders.</Text>
            <Text style={styles.colRate}>$0.1</Text>
          </View>
          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(3)</Text>
            <Text style={styles.colDescKhmer}>{`ថ្លៃទៅយកទំនិញក្នុងមួយទំនិញ គិតពីអ្នកលក់`}</Text>
            <Text style={styles.colDescEng}>
              Unit pickup fee for picking up the order SELLER prepares for the buyer at SELLER’s site.
            </Text>
            <Text style={styles.colRate}>$0.15</Text>
          </View>
          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(4)</Text>
            <Text
              style={styles.colDescKhmer}
            >{`ថ្លៃដាក់ស្តុកក្នុងឃ្លាំងក្នុងមួយម៉ែត្រគូប/ថ្ងៃ មិនគិតថ្លៃ 0.5 ម៉ែត្រគូបដំបូង`}</Text>
            <Text style={styles.colDescEng}>
              Storage fee per cubic meter per day, the first 0.5 cubic meter is free.
            </Text>
            <Text style={styles.colRate}>$0.33</Text>
          </View>
          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(5)</Text>
            <Text
              style={styles.colDescKhmer}
            >{`ថ្លៃរៀបទំនិញចូលស្តុកក្នុងមួយទំនិញ សម្រាប់ការងារបើកកញ្ចប់ ពិនិត្យ វាស់វែង បិទស្លាក និងដាក់ស្តុក`}</Text>
            <Text style={styles.colDescEng}>
              Inventory processing fee per unit for unpacking the shipment, verifying the dimension and weight of the
              unit, labeling the unit, and storing the unit on shelves.
            </Text>
            <Text style={styles.colRate}>$0.02</Text>
          </View>
          <View style={styles.feeRow}>
            <Text style={styles.colCode}>F(6)</Text>
            <Text
              style={styles.colDescKhmer}
            >{`ថ្លៃដកស្តុកក្នុងមួយទំនិញ សម្រាប់ប្រមូល វេចខ្ចប់ និងបញ្ជូនត្រឡប់ទៅអ្នកលក់វិញ`}</Text>
            <Text style={styles.colDescEng}>
              Inventory removal fee per unit for collecting the unit from shelf, packing the unit, and sending it to
              SELLER.
            </Text>
            <Text style={styles.colRate}>$0.05</Text>
          </View>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>9</Text>
          <Text style={styles.pointKhmer}>
            {`អ្នកលក់យល់ព្រមដោយស្វ័យប្រវត្តិចំពោះថ្លៃសេវា និងការគិតថ្លៃទាំងអស់របស់ប្រព័ន្ធ     
            ក្នុងចំណុច 8 ខាងលើ។ ក្នុងករណីមានការចរចាអត្រាថ្លៃសេវាខុសពីអត្រាខាងលើ     
            ប្រព័ន្ធនឹងបញ្ជាក់អត្រាពិសេសជាៀងរាល់ខែ ក្នុងប្រព័ន្ធ។ អត្រាចរចាខុសពីអត្រាស្តង់ដារត្រូវបន្តមួយខែម្តង។`}
          </Text>
          <Text style={styles.pointEng}>
            SELLER automatically agrees to fees and charges by PLATFORM in point 8 above. If there is a negotiated fee
            different from standard rates above, PLATFORM lists the negotiated fee monthly for relevant SELLER.
            Negotiated fees rate must be renewed monthly.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>10</Text>
          <Text style={styles.pointKhmer}>
            {`ប្រាក់ដែលទទួលបានពីអ្នកទិញ អាចដកបានក្រោយការដឹកជញ្ជូនរយៈពេល ១០ ថ្ងៃដោយសារអ្នកទិញមានសិទ្ធិប្រគល់ទំនិញវិញ ក្នុងរយៈពេល ៥ ថ្ងៃ។ 
            ប្រព័ន្ធនឹងដំណើរការទូទាត់ប្រាក់ក្នុងរយៈពេល ៧ ថ្ងៃធ្វើការ      
            បន្ទាប់ពីទទួលសំណើដកប្រាក់。`}
          </Text>
          <Text style={styles.pointEng}>
            Payments from buyers are available for disbursement in 10 days after delivery since buyers have a five-day
            period to return products which do not satisfy them. PLATFORM processes any disbursement within 7 working
            days after request.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>11</Text>
          <Text style={styles.pointKhmer}>
            {`ដើម្បីការពារការបោកប្រាស់ក្នុងការប្រគល់ទំនិញវិញ ប្រព័ន្ធតម្រូវឲ្យអ្នកទិញផ្តល់រូប     
            ថត ឬវីដេអូបញ្ជាក់ពីបញ្ហានៃទំនិញមុនពេលអនុម័តការប្រគល់វិញ。`}
          </Text>
          <Text style={styles.pointEng}>
            PLATFORM wants buyers to receive products that satisfy them. However, to prevent fraudulent returns,
            PLATFORM requires buyers to take a picture or video to prove that the product has issues before PLATFORM
            accepts any return.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>12</Text>
          <Text
            style={styles.pointKhmer}
          >{`អ្នកលក់ត្រូវចូលប្រើប្រព័ន្ធ ដើម្បីដាក់សំណើ សុំការទូទាត់ប្រាក់បានពីការលក់。`}</Text>
          <Text style={styles.pointEng}>
            SELLER shall log into PLATFORM’s tools to request disbursement before PLATFORM can process the disbursement.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>13</Text>
          <Text style={styles.pointKhmer}>
            {`អ្នកលក់ទទួលខុសត្រូវលើការបង់ពន្ធចំពោះអាជីវកម្មផ្ទាល់ខ្លួនបើតម្រូវដោយ      
            អាជ្ញាធរពន្ធដារកម្ពុជា ក្រុមហ៊ុនសន្មតថា តម្លៃដែលអ្នកលក់កំណត់បាន     
            រួមបញ្ចូលអាករលើតម្លៃបន្ថែម (VAT) រួចហើយបើអ្នកលក់មានលក្ខខណ្ឌ VAT ក្នុងអាជីវកម្មរបស់ខ្លួន ក្រុមហ៊ុនអាចចេញវិក្កយបត្រជូនអ្នកលក់បាន
            តែសម្រាប់ថ្លៃសេវាដែលក្រុមហ៊ុនបានទទួលពីអ្នកលក់តែប៉ុណ្ណោះ。`}
          </Text>
          <Text style={styles.pointEng}>
            SELLER is responsible for reporting tax-related information and paying taxes to the relevant tax authority
            in Cambodia if applicable. PLATFORM assumes that SELLER includes VAT in prices set by SELLER. PLATFORM only
            acts as an intermediary among buyers, sellers, operators, and drivers. PLATFORM can only issue a tax invoice
            to SELLER monthly for its charge share only.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>14</Text>
          <Text style={styles.pointKhmer}>
            {`ក្រុមហ៊ុនមានសិទ្ធិបិទហាងរបស់អ្នកលក់ក្នុងប្រព័ន្ធ បញ្ចប់កិច្ចព្រមព្រៀងនេះ     
            ដកទំនិញនៅក្នុងឃ្លាំងទុកជាកម្មសិទ្ធិក្រុមហ៊ុនឬបោះបង់ចោល រឹបអូសសមតុល្យ     
            របស់អ្នកលក់ដើម្បីទូទាត់បំណុល ប្រសិនបើអ្នកលក់ ដាក់លក់ទំនិញខុសច្បាប់     
            មិនទូទាត់ថ្លៃសេវាលើសពី ៣ ខែ ឬមិនមានសកម្មភាពលើសពី ១២ ខែ。 
            បើសមតុល្យមិនគ្រប់ ក្រុមហ៊ុនអាចរឹបអូស និងលក់ស្តុករបស់អ្នកលក់     
            ដើម្បីទូទាត់បំណុល。    `}
          </Text>
          <Text style={styles.pointEng}>
            PLATFORM has the right to close SELLER’s store on its platforms, terminate this agreement, remove and
            discard SELLER’s products in PLATFORM’s facilities or partner facilities, and seize SELLER’s balance to
            settle any charge from PLATFORM if SELLER (a) lists illegal products, (b) fails to settle any payment for
            three months, (c) or is inactive for more than 12 months. If SELLER’s balance is not enough to settle any
            charge from PLATFORM, PLATFORM can seize and sell off SELLER’s inventory.
          </Text>
        </View>

        <View style={styles.pointRow}>
          <Text style={styles.pointNum}>15</Text>
          <Text style={styles.pointKhmer}>
            {`ប្រសិនបើ អ្នកលក់ មិនបានស្នើសុំដកប្រាក់នៅជាមួយក្រុមហ៊ុនដែលបានពីការ     
            លក់ ឬទូទាត់បំណុលជំពាក់ក្រុមហ៊ុន រយៈពេលលើសពី ២ ឆ្នាំជាប់គ្នា ក្រុមហ៊ុនមាន     
            សិទ្ធិទូទាត់កាត់កងនិងលុបទឹកប្រាក់នៅជាមួយក្រុមហ៊ុនដែលបានពីការលក់ ហើយចាត់ទុកជាចំណូលក្រុមហ៊ុន។ អ្នកលក់ នឹងបាត់បង់សិទ្ធិក្នុងការទាមទារ     
            ទឹកប្រាក់ដែលបានលុបចោល ហើយក្រុមហ៊ុនមិនមានកាតព្វកិច្ចទូទាត់     
            ទឹកប្រាក់នេះឡើ។     `}
          </Text>
          <Text style={styles.pointEng}>
            PLATFORM shall write off SELLER’s account payable and account receivable if SELLER has not requested
            disbursement or settlement of such accounts for a continuous period longer than two (2) years. After this
            period, SELLER forfeits any claim to the written-off balances, and PLATFORM shall have no further obligation
            to disburse or collect such amounts.
          </Text>
        </View>

        {/* Agreement Sign / Details Section with RED dynamic text */}
        <View style={styles.agreementBox}>
          <View style={styles.agreementLine}>
            <Svg width={11} height={11} viewBox="0 0 12 12" style={{ marginRight: 5, marginTop: 1 }}>
              <Rect x="0.5" y="0.5" width="11" height="11" rx="1.5" stroke="#111111" strokeWidth="1" fill="#ffffff" />
              <Polyline
                points="2.5,6 5,8.5 9.5,3.5"
                stroke="#111111"
                strokeWidth="1.6"
                fill="none"
                strokeLineCap="round"
              />
            </Svg>
            <Text style={{ fontWeight: 'bold' }}>{`យល់ព្រមដោយអ្នកលក់ (Accepted and agreed by SELLER) `}</Text>
            <Text style={styles.redText}>{year}-01-01 </Text>
            <Text style={{ fontWeight: 'bold' }}>{`លេខសម្គាល់ (Seller ID) `}</Text>
            <Text style={styles.redText}>{supid} </Text>
            <Text style={{ fontWeight: 'bold' }}>{`ឈ្មោះហាង (Store) `}</Text>
            <Text style={styles.redText}>{name}</Text>
          </View>
          <View style={styles.agreementLine}>
            <Text style={{ fontWeight: 'bold' }}>{`តំណាងដោយ (Representative) `}</Text>
            <Text style={styles.redText}>{legalRep}</Text>
          </View>
          <View style={styles.agreementLine}>
            <Text style={{ fontWeight: 'bold' }}>{`លេខទូរស័ព្ទ (Phone) `}</Text>
            <Text style={styles.redText}>{phone}</Text>
          </View>
          <View style={styles.agreementLine}>
            <Text style={{ fontWeight: 'bold' }}>{`អ៊ីណម៉ែល (Email) `}</Text>
            <Text style={styles.redText}>{email || '________________'}</Text>
          </View>
        </View>

        {/* Monthly Negotiated Fee Sections (Months 01 to 12) */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => renderNegotiatedFeeSection(m))}
      </Page>
    </Document>
  );
}
